// The Assets page rows, shared by the web and mobile lists: one entry per
// asset (or per account / loan / broker cash under Cash), with its summary
// line, gain badge and value. Pure functions over the dashboard payload.
import { formatCurrency, formatUSD, FIXED_ASSET_CATEGORIES, LIQUID_ASSET_CATEGORIES, PERSONAL_ITEM_CATEGORIES } from "../constants";
import { holdingsSummary, portfolioGainPercent } from "./crypto";
import { goldSummary, goldGainPercent } from "./gold";
import { govtSummary, govtGainPercent } from "./govt";
import { bondSummary, bondGainPercent } from "./bonds";
import { mfSummary, mfGainPercent } from "./mf";
import { ledgerBrokers } from "./stocks";
import { groupOfAsset, groupValue, brokerCashINR, stockFxRate } from "./groups";
import { getTravelProgramDisplayRow } from "./travelPointsPrograms";

const usd2 = (v) => formatUSD(v, { decimals: 2, suffix: "" });
const CURRENCY_SIGN = { INR: "₹", USD: "$", EUR: "€" };

/** A whole asset as one row: summary line and gain by asset type. The US
 *  ledger shows its holdings only; its uninvested cash is a Cash row. */
export function assetRow(a, cryptoPrices) {
  const type = a.asset_type;
  let meta = "";
  let gain = null;
  switch (type) {
    case "crypto":
      if (a.crypto_holdings?.length) {
        meta = holdingsSummary(a) + (a.total_value_usd > 0 ? ` · ${usd2(a.total_value_usd)}` : "");
        gain = portfolioGainPercent(a, cryptoPrices);
      }
      break;
    case "mutual_fund":
      if (a.mf_holdings?.length) {
        meta = mfSummary(a);
        gain = mfGainPercent(a);
      }
      break;
    case "us_stocks": {
      const holdings = a.stock_holdings || [];
      const brokers = a.ledger ? ledgerBrokers(a).length : 0;
      const usd = holdings.reduce((s, h) => s + h.units * (h.last_price || 0), 0);
      const n = new Set(holdings.map((h) => h.symbol)).size;
      meta = `${n} holding${n === 1 ? "" : "s"}${brokers > 1 ? ` at ${brokers} brokers` : ""} · ${usd2(usd)}`;
      if (a.cash_usd > 0) meta += ` · ${usd2(a.cash_usd)} cash shown under Cash`;
      gain = stockHoldingsGainPercent(a);
      break;
    }
    case "gold":
      if (a.gold_holdings?.length) {
        meta = goldSummary(a);
        gain = goldGainPercent(a);
      }
      break;
    case "govt_schemes":
      if (a.govt_holdings?.length) {
        meta = govtSummary(a);
        gain = govtGainPercent(a);
      }
      break;
    case "bonds":
      if (a.bond_holdings?.length) {
        meta = bondSummary(a);
        gain = bondGainPercent(a);
      }
      break;
    case "personal":
      meta = `Bought for ${formatCurrency(a.purchase_price ?? a.current_value)}`;
      if (a.purchase_price > 0) gain = ((a.current_value - a.purchase_price) / a.purchase_price) * 100;
      break;
    default:
      break;
  }
  return {
    key: a.id, asset: a, name: a.name, chip: a.category, meta, gain,
    value: groupValue(a), sub: null, canDelete: true, isLedger: type === "us_stocks" && !!a.ledger,
  };
}

/** Gain on the stock holdings alone (USD buy price vs last price), so the
 *  Investments row matches the holdings-only value it shows. */
export function stockHoldingsGainPercent(a) {
  let invested = 0;
  let current = 0;
  (a.stock_holdings || []).forEach((h) => {
    if (h.buy_price_usd > 0 && h.last_price > 0) {
      invested += h.units * h.buy_price_usd;
      current += h.units * h.last_price;
    }
  });
  return invested > 0 ? ((current - invested) / invested) * 100 : null;
}

/** One bank account as a Cash row; foreign balances show the amount and rate. */
export function bankRow(a, h, i) {
  const sign = CURRENCY_SIGN[h.currency] || "";
  const foreign = h.currency && h.currency !== "INR";
  const rate = h.last_rate > 0 ? h.last_rate : 1;
  const value = foreign ? h.balance * rate : h.balance;
  let meta = "";
  let gain = null;
  if (foreign) {
    meta = `${sign}${Number(h.balance).toLocaleString("en-US")} · ₹${rate.toFixed(2)}/${sign}`;
    if (h.buy_rate > 0) {
      meta += ` today, bought at ₹${Number(h.buy_rate).toFixed(2)}`;
      gain = ((rate - h.buy_rate) / h.buy_rate) * 100;
    }
  }
  return {
    key: `${a.id}:bank:${i}`, asset: a, name: h.label || a.name, chip: a.category, meta, gain,
    value, sub: foreign ? `${sign}${Number(h.balance).toLocaleString("en-US")}` : null, canDelete: false, isLedger: false,
  };
}

/** One loan given out as a Cash row. */
export function loanRow(a, h, i) {
  return {
    key: `${a.id}:loan:${i}`, asset: a, name: h.label || a.name, chip: "Loan", meta: "Owed to you", gain: null,
    value: h.amount_inr || 0, sub: null, canDelete: false, isLedger: false,
  };
}

/** Uninvested USD at one broker as a Cash row; edits go through the US modal. */
export function brokerCashRow(a, c, i) {
  const fx = stockFxRate(a);
  return {
    key: `${a.id}:broker:${i}`, asset: a, name: `${c.broker} cash`, chip: "Broker",
    meta: `Uninvested USD at ${c.broker} · ₹${fx.toFixed(2)}/$`, gain: null,
    value: c.usd * fx, sub: usd2(c.usd), canDelete: false, isLedger: !!a.ledger,
  };
}

/** Rows for one group (not travel), ranked by value. */
export function groupRows(assets, group, cryptoPrices = []) {
  const list = [];
  assets.forEach((a) => {
    if (groupOfAsset(a) === group) {
      if (group === "cash" && a.asset_type === "bank_accounts" && a.bank_holdings?.length) {
        a.bank_holdings.forEach((h, i) => list.push(bankRow(a, h, i)));
      } else if (group === "cash" && a.asset_type === "loans" && a.loan_holdings?.length) {
        a.loan_holdings.forEach((h, i) => list.push(loanRow(a, h, i)));
      } else {
        list.push(assetRow(a, cryptoPrices));
      }
    }
    // Uninvested USD at a broker is cash, wherever the portfolio lives.
    if (group === "cash" && brokerCashINR(a) > 0) {
      (a.broker_cash?.length ? a.broker_cash : [{ broker: "Broker", usd: a.cash_usd }]).forEach((c, i) =>
        list.push(brokerCashRow(a, c, i))
      );
    }
  });
  return list.sort((x, y) => y.value - x.value);
}

/** Travel points: one ranked row per loyalty programme. */
export function travelRows(assets, usdRate) {
  const rows = [];
  assets.filter((a) => groupOfAsset(a) === "travel").forEach((asset) => {
    const programs = asset.travel_points_programs || [];
    if (programs.length > 0) {
      programs.forEach((raw) => {
        const p = getTravelProgramDisplayRow(raw);
        const usd = p.points * p.points_per_value;
        rows.push({ asset, name: p.program || asset.name, pts: p.points, rate: p.points_per_value, usd, inr: usd * usdRate });
      });
    } else {
      const inr = asset.total_value_inr || asset.current_value || 0;
      rows.push({ asset, name: asset.name, pts: 0, rate: 0, usd: asset.total_value_usd || inr / usdRate, inr });
    }
  });
  return rows.sort((a, b) => b.inr - a.inr);
}

/** Which categories the add dialog offers for a group's asset type. */
export function categoriesForType(type) {
  if (type === "fixed") return FIXED_ASSET_CATEGORIES;
  if (type === "personal") return PERSONAL_ITEM_CATEGORIES;
  return LIQUID_ASSET_CATEGORIES;
}

/** The add dialog's asset type for a group. */
export const dialogTypeForGroup = (group) => (group === "property" ? "fixed" : group === "personal" ? "personal" : "liquid");
